import { useEffect, useMemo, useState } from 'react';
import { App } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KmsDocumentService } from '~/app/services/kms/kms-document.service';
import type { GetKmsDocumentStatusResponse } from '~/app/services/kms/kms-document.types';

function toEditableRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {};
  }
  return { ...(value as Record<string, unknown>) };
}

export function useKmsExtractedFieldsEditor(params: {
  document: GetKmsDocumentStatusResponse | null;
  organizationId: string | null;
  isSuperAdmin: boolean;
}) {
  const { document, organizationId, isSuperAdmin } = params;
  const { t } = useTranslation('kms');
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const [draftJson, setDraftJson] = useState('{}');
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    setDraftJson(
      JSON.stringify(toEditableRecord(document?.extractedFields), null, 2),
    );
    setParseError(null);
  }, [
    document?.documentId,
    document?.extractedFields,
    document?.fieldsConfirmedAt,
  ]);

  const flaggedFields = useMemo(() => {
    if (!Array.isArray(document?.flaggedFields)) {
      return [] as string[];
    }
    return document.flaggedFields.filter(
      (entry): entry is string => typeof entry === 'string',
    );
  }, [document?.flaggedFields]);

  const canEdit = Boolean(
    document &&
    (document.status === 'linked' || document.status === 'write_failed') &&
    document.extractedFields != null &&
    !document.fieldsConfirmedAt,
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!document) {
        throw new Error('missing document');
      }
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(draftJson) as Record<string, unknown>;
      } catch {
        throw new Error(t('detail.extractedFields.invalidJson'));
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error(t('detail.extractedFields.invalidJson'));
      }
      return KmsDocumentService.updateExtractedFields({
        documentId: document.documentId,
        extractedFields: parsed,
        organizationId,
        isSuperAdmin,
      });
    },
    onSuccess: async () => {
      setParseError(null);
      notification.success({
        message: t('detail.extractedFields.saveSuccess'),
      });
      await queryClient.invalidateQueries({
        queryKey: ['kms', 'document', document?.documentId],
      });
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : t('detail.extractedFields.saveError');
      setParseError(message);
      notification.error({
        message: t('detail.extractedFields.saveError'),
        description: message,
      });
    },
  });

  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!document) {
        throw new Error('missing document');
      }
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(draftJson) as Record<string, unknown>;
      } catch {
        throw new Error(t('detail.extractedFields.invalidJson'));
      }
      return KmsDocumentService.confirmExtractedFields({
        documentId: document.documentId,
        extractedFields: parsed,
        organizationId,
        isSuperAdmin,
      });
    },
    onSuccess: async () => {
      setParseError(null);
      notification.success({
        message: t('detail.extractedFields.confirmSuccess'),
      });
      await queryClient.invalidateQueries({
        queryKey: ['kms', 'document', document?.documentId],
      });
    },
    onError: (error) => {
      const message =
        error instanceof Error
          ? error.message
          : t('detail.extractedFields.confirmError');
      setParseError(message);
      notification.error({
        message: t('detail.extractedFields.confirmError'),
        description: message,
      });
    },
  });

  return {
    canEdit,
    draftJson,
    setDraftJson,
    parseError,
    flaggedFields,
    extractionConfidence: document?.extractionConfidence ?? null,
    fieldsConfirmedAt: document?.fieldsConfirmedAt ?? null,
    hasExtractedFields: document?.extractedFields != null,
    isSaving: saveMutation.isPending,
    isConfirming: confirmMutation.isPending,
    onSave: () => void saveMutation.mutateAsync(),
    onConfirm: () => void confirmMutation.mutateAsync(),
  };
}
